import {
    getSingleEventValue,
    useSchedulingValue,
} from '~/lib/Questions/SchedulingHelpers';
import { useEffect, useMemo, useState } from 'react';
import {
    addDays,
    addMonths,
    endOfDay,
    endOfMonth,
    format,
    isSameDay,
    isSameWeek,
    isToday,
    startOfDay,
    startOfMonth,
    subDays,
    subMonths,
} from 'date-fns';
import {
    useBookableResourcesQuery,
    useClassAvailabilityQuery,
    useEventTypesQuery,
} from '~/lib/SharedQueries';
import { isMobile } from '~/lib/Utils.js';
import ClassWeeklyDayNavigation from '~/components/forms/questions/scheduling/ClassWeeklyDayNavigation';
import Loading from '~/components/Loading';
import ClassList from '~/components/forms/questions/scheduling/ClassList';
import SelectionSelectedBlock from '~/components/forms/questions/scheduling/SelectionSelectedBlock.jsx';
import NoAvailableClasses from '~/components/forms/questions/scheduling/NoAvailableClasses';
import QuantityPicker from '~/components/forms/questions/scheduling/QuantityPicker';
import ClassSelectionSummary from '~/components/forms/questions/scheduling/ClassSelectionSummary';
import WeeklyClassList from '~/components/forms/questions/scheduling/WeeklyClassList';
import { useWindowSize } from 'usehooks-ts';
import { isAdminApp } from '~/public/lib/PageHelpers';
import ClassMonthlyView from '~/components/forms/questions/scheduling/ClassMonthlyView.jsx';
import ClassMonthlyDayNavigation from '~/components/forms/questions/scheduling/ClassMonthlyDayNavigation.jsx';
import useClientTimezone from '~/public/lib/useClientTimezone.js';
import { clientFormatDateTimeLong } from '~/public/lib/ClientDateTime.js';

const VIEW_WEEKLY = 'weekly';
const VIEW_MONTHLY = 'monthly';

const SelectionSummary = ({ value, settings, onEdit, clientTz }) => {
    const { data: eventTypesData } = useEventTypesQuery();
    const { data: bookableResourceData } = useBookableResourcesQuery();

    function findBookableResource(id) {
        return (bookableResourceData || []).find(br => br.id == id);
    }

    function findEventType(id) {
        return (eventTypesData || []).find(et => et.id == id);
    }

    const selection = value[0];
    const eventType = findEventType(selection.eventType);

    return (
        <SelectionSelectedBlock
            onChange={onEdit}
            title={
                <span>
                    {eventType?.name || ''}{' '}
                    <span className="font-normal text-gray-500 block">
                        {clientFormatDateTimeLong(
                            selection.startTime,
                            clientTz,
                        )}
                    </span>
                </span>
            }
            price={eventType?.price}
        />
    );
};

const ClassSchedulingQuestion = ({ value, setValue, settings }) => {
    const [quantity, setQuantity] = useState(1);
    const [capacity, setCapacity] = useState(1);
    const [selectedClassMetadata, setSelectedClassMetadata] = useState([]);
    const clientTz = useClientTimezone();
    const { update, add } = useSchedulingValue({
        setValue,
        value,
        setQuantity,
        quantity,
        capacity,
        settings,
    });

    let view = settings?.view || VIEW_WEEKLY;
    if (view !== VIEW_WEEKLY && view !== VIEW_MONTHLY) {
        // Default to weekly
        view = VIEW_WEEKLY;
    }

    const { width } = useWindowSize();
    const isNarrowWindow = width < 1100 || isAdminApp();
    const totalViewDays = view === VIEW_MONTHLY ? 31 : 7 - 1;

    function calculateEnd(start) {
        if (view === VIEW_MONTHLY) {
            return endOfMonth(start);
        } else {
            return endOfDay(addDays(start, totalViewDays));
        }
    }

    const [start, setStart] = useState(startOfDay(new Date()));
    const [end, setEnd] = useState(calculateEnd(start));
    const [selectedDate, setSelectedDate] = useState(null);
    const isAddingTimes = !!settings?.enableRecurring;

    function setViewStart(newStart) {
        if (view === VIEW_MONTHLY) {
            newStart = startOfMonth(newStart);
        }

        setStart(startOfDay(newStart));
        setEnd(calculateEnd(newStart));
    }

    const directLinkEventType = useMemo(() => {
        const uri = new URL(document.location.href);
        return uri.searchParams.get('eventType');
    }, []);

    // If we're direct linking to an event type then we only show that one
    const onlyShowEventTypes = directLinkEventType
        ? [directLinkEventType]
        : settings?.eventTypes;

    const { data, isLoading } = useClassAvailabilityQuery(
        start,
        end,
        clientTz,
        {
            filterEventTypeIds: onlyShowEventTypes,
            filterBookableResourceIds: settings?.bookableResources,
        },
    );
    const allClasses = (data?.classes || []).filter(i => {
        return !settings?.hideFullClasses || i.spots_left > 0;
    });
    const selectedDateClasses = allClasses.filter(
        i => selectedDate && isSameDay(i.start, selectedDate),
    );

    const singleEventValue = getSingleEventValue(value);
    useEffect(() => {
        if (singleEventValue?.capacity > 1) {
            setCapacity(singleEventValue?.capacity);
        }
    }, [singleEventValue?.capacity]);

    useEffect(() => {
        setViewStart(start < new Date() ? new Date() : start);
    }, [view]);

    useEffect(() => {
        // Make sure we default to the first selected date with classes on it
        if (!selectedDateClasses?.length) {
            // Find the first date with classes
            if (allClasses?.length) {
                setSelectedDate(startOfDay(new Date(allClasses[0].start)));
                setSelectedDate(startOfDay(new Date(allClasses[0].start)));
            } else {
                setSelectedDate(null);
            }
        }
    }, [allClasses]);

    useEffect(() => {
        // Make sure client time zone is set
        if (value && Array.isArray(value)) {
            let newValue = [...value];
            let changed = false;
            for (let i in newValue) {
                if (typeof newValue[i]?.clientTimezone === 'undefined') {
                    newValue[i].clientTimezone = clientTz || '';
                    changed = true;
                }
            }

            if (changed) {
                setValue(newValue);
            }
        }
    }, [value]);

    if (!isAddingTimes && value && value[0]?.startTime) {
        return (
            <>
                <SelectionSummary
                    value={value}
                    settings={settings}
                    onEdit={() => setValue(null)}
                    clientTz={clientTz}
                />

                <QuantityPicker
                    value={quantity}
                    onChange={setQuantity}
                    capacity={capacity}
                    settings={settings}
                />
            </>
        );
    }

    function onSelectClass(i) {
        setCapacity(i.spots_left);
        setSelectedClassMetadata([...selectedClassMetadata, i]);
        add({
            bookableResource: i.bookable_resource_id,
            eventType: i.event_type_id,
            startTime: i.start,
            clientTimezone: clientTz,
        });
    }

    function onRemoveClass(i) {
        setValue(old => {
            return old.filter(oldInstance => {
                return !(
                    oldInstance.bookableResource === i.bookable_resource_id &&
                    oldInstance.eventType === i.event_type_id &&
                    oldInstance.startTime === i.start
                );
            });
        });
    }

    return (
        <div
            className={`${settings?.isError ? 'border-l-2 border-red-600 pl-4' : ''}`}
        >
            {!isNarrowWindow && view === VIEW_MONTHLY && (
                <ClassMonthlyView
                    start={start}
                    end={end}
                    onPrevious={() => {
                        setViewStart(subMonths(start, 1));
                    }}
                    onNext={() => {
                        setViewStart(addMonths(start, 1));
                    }}
                    settings={settings}
                    selectedClasses={value}
                    clientTz={clientTz}
                    onSelectClass={onSelectClass}
                    onRemoveClass={onRemoveClass}
                    classes={allClasses}
                    noAvailabilityComponent={
                        <div className="absolute w-full h-full mb-16 z-10 bg-[#FFFFFF99] flex items-center justify-center">
                            <NoAvailableClasses
                                nextAvailableDate={data?.nextAvailableDate}
                                nextAvailableWaitlist={
                                    data?.nextAvailableWaitlist
                                }
                                settings={settings}
                                onViewDate={setViewStart}
                                className="mb-32"
                            />
                        </div>
                    }
                />
            )}

            {isNarrowWindow && view === VIEW_MONTHLY && (
                <ClassMonthlyDayNavigation
                    start={start}
                    end={end}
                    classes={allClasses}
                    selectedDate={selectedDate}
                    setSelectedDate={setSelectedDate}
                    setViewStart={setViewStart}
                    onPrevious={() => {
                        setViewStart(subMonths(start, 1));
                    }}
                    onNext={() => {
                        setViewStart(addMonths(start, 1));
                    }}
                />
            )}

            {isNarrowWindow && view === VIEW_WEEKLY && (
                <ClassWeeklyDayNavigation
                    start={start}
                    end={end}
                    classes={allClasses}
                    selectedDate={selectedDate}
                    setSelectedDate={setSelectedDate}
                    onPrevious={() => {
                        setViewStart(subDays(start, totalViewDays + 1));
                    }}
                    onNext={() => {
                        setViewStart(addDays(start, totalViewDays + 1));
                    }}
                />
            )}

            {!isNarrowWindow && view === VIEW_WEEKLY && (
                <WeeklyClassList
                    allClasses={allClasses}
                    start={start}
                    end={end}
                    onPrevious={() => {
                        setViewStart(subDays(start, totalViewDays + 1));
                    }}
                    onNext={() => {
                        setViewStart(addDays(start, totalViewDays + 1));
                    }}
                    selectedClasses={value}
                    onSelectClass={onSelectClass}
                    onRemoveClass={onRemoveClass}
                    settings={settings}
                    clientTz={clientTz}
                />
            )}

            {isLoading && <Loading className="mt-4" />}
            {!isLoading &&
                !selectedDateClasses?.length &&
                (view === VIEW_WEEKLY || isNarrowWindow) && (
                    <NoAvailableClasses
                        nextAvailableDate={data?.nextAvailableDate}
                        nextAvailableWaitlist={data?.nextAvailableWaitlist}
                        settings={settings}
                        onViewDate={setViewStart}
                    />
                )}

            {!isLoading && (
                <div>
                    {isNarrowWindow && (
                        <ClassList
                            classes={selectedDateClasses}
                            settings={settings}
                            selectedClasses={value}
                            onSelectClass={onSelectClass}
                            onRemoveClass={onRemoveClass}
                            clientTz={clientTz}
                        />
                    )}

                    <QuantityPicker
                        value={quantity}
                        onChange={setQuantity}
                        capacity={capacity}
                        settings={settings}
                    />

                    <ClassSelectionSummary
                        className="mt-4"
                        selectedClasses={value}
                        settings={settings}
                        selectedClassMetadata={selectedClassMetadata}
                        clientTz={clientTz}
                        onRemoveClass={toRemove => {
                            setValue(old => {
                                return old.filter(oldInstance => {
                                    return !(
                                        oldInstance.bookableResource ===
                                            toRemove.bookableResource &&
                                        oldInstance.eventType ===
                                            toRemove.eventType &&
                                        oldInstance.startTime ===
                                            toRemove.startTime
                                    );
                                });
                            });
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default ClassSchedulingQuestion;
